/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import { TIPO_USUARIO } from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useClinica } from '../../../hooks/clinica';
import { useAuth } from '../../../hooks/auth';

import Input from '../../../components/Input';
import Select from '../../../components/Select';
import Loading from '../../../components/Loading';
import PageInfo from '../../../components/PageInfo';

import getValidationErrors from '../../../utils/getValidationErrors';

import HeaderRelatorio from '../HeaderRelatorio';
import getResponseErrors from '../../../utils/getResponseErrors';

interface IUsuariosNomeCodigo {
  UsuNome: string;
  UsuCodigo: Number;
}

interface IRelatorio extends IUsuariosNomeCodigo {
  totalAgendamentos: number;
  totalCobranca: number;
  totalCartao: number;
  totalRecebimentos: number;
}

interface IDataBusca {
  dataInicio: string;
  dataFim: string;
}

interface IFormBusca {
  dataInicio: Date;
  dataFim: Date;
  CliCodigo: string;
  UsuCodigo: string;
}

interface IDataExibe {
  dataHora: string;
  dataDiaSemana: string;
}

const Produtividade: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();
  const { optionsClinica } = useClinica();

  useEffect(() => {
    if (user.UsuTipo > TIPO_USUARIO.SUPERVISOR) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const informacoesPagina = `<b>Descrição:</b> Exibe a quantidade de operações que os colaboradores realizaram no sistema, quantos agendamentos, quantas operações com cartão saúde (inclusões, exclusões, alteração de vencimento entre outras), quantas cobranças foram realizadas e quantos recebimentos foram feitos. `;

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IRelatorio[]>();

  const initialData = {
    dataInicio: format(new Date(), 'yyyy-MM-dd'),
    dataFim: format(new Date(), 'yyyy-MM-dd'),
    CliCodigo: user.UsuClinica.toString(),
  };
  const [dataExibe, setDataExibe] = useState<IDataExibe>();
  const [dataBusca, setDataBusca] = useState<IDataBusca>();
  const [clinica, setClinica] = useState('');

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          dataInicio: Yup.date().required(),
          dataFim: Yup.date().required(),
          CliCodigo: Yup.string().nullable(true),
          UsuCodigo: Yup.string().nullable(true),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setClinica(
          optionsClinica.find(cli => cli.value === dados.CliCodigo)?.label ||
            '',
        );

        const dataInicioSelecionada = parseISO(dados.dataInicio.toString());
        const dataFimSelecionada = parseISO(dados.dataFim.toString());

        const dataInicioFormatada = format(dataInicioSelecionada, 'yyyy-MM-dd');
        const dataFimFormatada = format(dataFimSelecionada, 'yyyy-MM-dd');

        setDataBusca({
          dataInicio: dataInicioFormatada,
          dataFim: dataFimFormatada,
        });

        let parametrosOpcionais = dados.UsuCodigo
          ? `&UsuCodigo=${dados.UsuCodigo}`
          : '';
        parametrosOpcionais += dados.CliCodigo
          ? `&CliCodigo=${dados.CliCodigo}`
          : '';

        const response = await api.get<IRelatorio[]>(
          `/relatorios/produtividade?dataInicio=${dataInicioFormatada}&dataFim=${dataFimFormatada}${parametrosOpcionais}`,
        );

        setDataExibe({
          dataDiaSemana: `${format(
            dataInicioSelecionada,
            'dd/MM/yyyy',
          )} até ${format(dataFimSelecionada, 'dd/MM/yyyy')}`,
          dataHora: format(new Date(), 'dd/MM/yyyy HH:mm:ss'),
        });

        const total: IRelatorio = {
          UsuCodigo: -1,
          UsuNome: 'TOTAL',
          totalAgendamentos: response.data.reduce((tot, item) => {
            return tot + Number(item.totalAgendamentos);
          }, 0),
          totalCartao: response.data.reduce((tot, item) => {
            return tot + Number(item.totalCartao);
          }, 0),
          totalCobranca: response.data.reduce((tot, item) => {
            return tot + Number(item.totalCobranca);
          }, 0),
          totalRecebimentos: response.data.reduce((tot, item) => {
            return tot + Number(item.totalRecebimentos);
          }, 0),
        };

        const retorno = response.data.concat([total]);

        setDadosRelatorio(retorno);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefBusca.current?.setErrors(errors);
        } else {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível carregar dados',
            description: mensagem,
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast, optionsClinica],
  );

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2 d-flex justify-content-between">
            <h2 id="topoBusca">Produtividade</h2>{' '}
            <PageInfo
              descricao={informacoesPagina}
              permissao={TIPO_USUARIO.SUPERVISOR}
            />
          </div>
          {optionsClinica.length > 0 && user && (
            <Form
              ref={formRefBusca}
              initialData={initialData}
              onSubmit={handleSubmitBusca}
            >
              <Row>
                <Col md={3}>
                  <Input name="dataInicio" label="De" type="date" required />
                </Col>
                <Col md={3}>
                  <Input name="dataFim" label="Até" type="date" required />
                </Col>
                <Col md={3}>
                  <Select
                    name="CliCodigo"
                    label="Clinica"
                    options={optionsClinica}
                    disabled={user.UsuTipo !== TIPO_USUARIO.ADMINISTRADOR}
                  />
                </Col>
                <Col md={3}>
                  <br />
                  <Button type="submit" size="sm" variant="outline-secondary">
                    BUSCAR
                  </Button>
                </Col>
              </Row>
            </Form>
          )}
        </div>

        <HeaderRelatorio
          dataImpressao={dataExibe?.dataHora}
          titulo={`PRODUTIVIDADE ${
            clinica.length > 0 ? `- ${clinica} -` : '-'
          } ${dataExibe?.dataDiaSemana || ''}`}
        />

        <div className="bg-white border p-2">
          {dadosRelatorio && (
            <>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th>Colaborador</th>
                    <th>Agendamentos</th>
                    <th>Cartão Saúde</th>
                    <th>Cobrança</th>
                    <th>Lanç. Financeiros</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <tr
                        key={`usua-${rel.UsuCodigo}`}
                        style={{ fontSize: '.7rem', height: '30px' }}
                      >
                        <td>
                          <Link
                            to={`produtividadedetalhe?UsuCodigo=${rel.UsuCodigo}&dataInicio=${dataBusca?.dataInicio}&dataFim=${dataBusca?.dataFim}`}
                            className="text-dark"
                            style={{ textDecoration: 'none' }}
                          >
                            {rel.UsuNome}
                          </Link>
                        </td>
                        <td>{rel.totalAgendamentos}</td>
                        <td>{rel.totalCartao}</td>
                        <td>{rel.totalCobranca}</td>
                        <td>{rel.totalRecebimentos}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </>
    </Container>
  );
};

export default Produtividade;
